import React from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { ThemeProvider, createMuiTheme } from '@material-ui/core/styles';
import NetworkDetector from '../components/NetworkDetector/NetworkDetector';
import Routes from './Routes';
import APP_FONT from '../helpers/font';

// UI-only theme refresh (no data/API/behavior changes). Keeps the BrickEZ
// brand navy while modernizing typography, shape, elevation, and the shared
// MUI controls (buttons, inputs, cards, tables) so every screen refreshes
// from this one place.
const BRAND = {
  navy: '#2E3B6E',
  navyDark: '#1B2547',
  navyLight: '#5A69A6',
  accent: '#4582FF',
  accentDark: '#2f6bea',
};

const theme = createMuiTheme({
  palette: {
    primary: {
      main: BRAND.navy,
      dark: BRAND.navyDark,
      light: BRAND.navyLight,
      contrastText: '#ffffff',
    },
    secondary: {
      main: BRAND.accent,
      dark: BRAND.accentDark,
      contrastText: '#ffffff',
    },
    background: {
      default: '#F4F6FB',
      paper: '#ffffff',
    },
    text: {
      primary: '#1F2733',
      secondary: '#5B6472',
    },
  },
  typography: {
    fontFamily: APP_FONT,
    h1: { fontWeight: 700 },
    h2: { fontWeight: 700 },
    h3: { fontWeight: 700 },
    h4: { fontWeight: 700 },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700 },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    button: { fontWeight: 600, textTransform: 'none', letterSpacing: 0.2 },
  },
  shape: { borderRadius: 10 },
  overrides: {
    MuiCssBaseline: {
      '@global': {
        body: { backgroundColor: '#F4F6FB' },
      },
    },
    MuiButton: {
      root: {
        borderRadius: 10,
        textTransform: 'none',
        fontWeight: 600,
        paddingTop: 8,
        paddingBottom: 8,
        boxShadow: 'none',
      },
      contained: {
        boxShadow: '0 1px 2px rgba(27, 37, 71, 0.16)',
        '&:hover': { boxShadow: '0 3px 8px rgba(27, 37, 71, 0.22)' },
      },
      containedPrimary: {
        background: `linear-gradient(180deg, ${BRAND.navyLight} 0%, ${BRAND.navy} 100%)`,
        '&:hover': { background: BRAND.navyDark },
      },
    },
    MuiPaper: {
      rounded: { borderRadius: 12 },
      elevation1: { boxShadow: '0 1px 3px rgba(16, 36, 70, 0.08), 0 1px 2px rgba(16, 36, 70, 0.06)' },
      elevation2: { boxShadow: '0 2px 8px rgba(16, 36, 70, 0.10)' },
    },
    MuiCard: {
      root: {
        borderRadius: 14,
        border: '1px solid #EAEEF5',
        boxShadow: '0 1px 3px rgba(16, 36, 70, 0.08)',
      },
    },
    MuiOutlinedInput: {
      root: {
        borderRadius: 10,
        backgroundColor: '#ffffff',
        '& $notchedOutline': { borderColor: '#D7DEEA' },
        '&:hover $notchedOutline': { borderColor: BRAND.navyLight },
        '&$focused $notchedOutline': { borderColor: BRAND.accent, borderWidth: 1 },
      },
    },
    MuiTableHead: {
      root: { backgroundColor: '#F2F5FA' },
    },
    MuiTableCell: {
      head: {
        fontWeight: 600,
        color: '#3A4453',
        borderBottom: '1px solid #E3E9F2',
      },
    },
    MuiAppBar: {
      colorPrimary: { backgroundColor: BRAND.navy },
    },
    MuiChip: {
      root: { borderRadius: 8, fontWeight: 600 },
    },
  },
});

const App = () => (
  <ThemeProvider theme={theme}>
    <Router>
      <Routes />
    </Router>
  </ThemeProvider>
);

export default NetworkDetector(App);
